import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  Heart, Clock, BookOpen, Award, TrendingUp, CheckCircle, ArrowRight, 
  Phone, Users, IndianRupee, Star, Play, ChevronDown, Calendar,
  GraduationCap, Shield, Zap, Target, MessageSquare, Home, Briefcase,
  Quote, Check, MapPin, Mail
} from 'lucide-react';
import Footer from '../components/Footer';
import Navigation from '../components/Navigation';

const ObserverLanding = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [earnings, setEarnings] = useState({ hours: 2, days: 5 });
  const [showVideo, setShowVideo] = useState(false);

  // Earnings calculator
  const ratePerSession = 87.50; // Average rate
  const sessionsPerHour = 4; // 15 min sessions
  const monthlyEarnings = earnings.hours * sessionsPerHour * earnings.days * 4 * ratePerSession;

  const stats = [
    { value: '500+', label: 'Active Observers', icon: Users },
    { value: '₹45K', label: 'Avg Monthly Earnings', icon: IndianRupee },
    { value: '4.8/5', label: 'Observer Satisfaction', icon: Star },
    { value: '95%', label: 'Retention Rate', icon: TrendingUp }
  ];

  const benefits = [
    { icon: IndianRupee, title: 'Competitive Pay', desc: '₹62.50 - ₹175 per session based on experience' },
    { icon: Clock, title: 'Flexible Hours', desc: 'Work from home, choose your own schedule' },
    { icon: Heart, title: 'Meaningful Work', desc: 'Make a real difference in children\'s lives' },
    { icon: GraduationCap, title: 'Free Training', desc: 'Comprehensive certification program included' },
    { icon: TrendingUp, title: 'Career Growth', desc: 'Progress from Bronze to Master Observer' },
    { icon: Shield, title: 'Job Security', desc: 'Consistent work with growing demand' }
  ];

  const steps = [
    { num: 1, title: 'Apply Online', desc: 'Fill out a simple application form (5 mins)', time: 'Day 1' },
    { num: 2, title: 'Complete Assessment', desc: 'Take our aptitude & empathy assessment', time: 'Day 1-2' },
    { num: 3, title: 'Interview', desc: 'Brief video call with our team', time: 'Day 3-5' },
    { num: 4, title: 'Training', desc: 'Complete our online certification program', time: 'Week 1-2' },
    { num: 5, title: 'Start Earning', desc: 'Get assigned children and begin sessions', time: 'Week 3' }
  ];

  const certifications = [
    { level: 'Bronze', sessions: '0-50', rate: '₹62.50', color: 'from-amber-600 to-amber-700', icon: '🥉' },
    { level: 'Silver', sessions: '51-150', rate: '₹75.00', color: 'from-gray-400 to-gray-500', icon: '🥈' },
    { level: 'Gold', sessions: '151-300', rate: '₹100.00', color: 'from-yellow-400 to-yellow-500', icon: '🥇' },
    { level: 'Master', sessions: '300+', rate: '₹175.00', color: 'from-purple-500 to-purple-600', icon: '👑' }
  ];

  const testimonials = [
    {
      name: 'Priya Sharma',
      role: 'Gold Observer, Mumbai',
      image: 'P',
      text: 'I was a stay-at-home mom looking for flexible work. Sanjaya gave me purpose and income. I now earn ₹35,000/month working just 3 hours daily!',
      rating: 5,
      earnings: '₹35,000/month'
    },
    {
      name: 'Kavita Jaiswal',
      role: 'Master Observer, Delhi',
      image: 'K',
      text: 'The training was excellent and the support from principals is amazing. I\'ve helped over 200 children in 2 years and it\'s the most fulfilling work.',
      rating: 5,
      earnings: '₹52,000/month'
    },
    {
      name: 'Anita Verma',
      role: 'Silver Observer, Bangalore',
      image: 'A',
      text: 'As a retired teacher, this is perfect. I work 2 hours a day, connect with children, and supplement my pension. Highly recommended!',
      rating: 5,
      earnings: '₹18,000/month'
    }
  ];

  const faqs = [
    {
      q: 'What qualifications do I need?',
      a: 'We look for empathy, patience, and good communication skills. A background in education, psychology, or child care is helpful but not required. We provide comprehensive training.'
    },
    {
      q: 'How much can I really earn?',
      a: 'Earnings depend on hours worked and experience level. New observers start at ₹62.50/session. With experience, you can earn up to ₹175/session. Most full-time observers earn ₹30,000-₹50,000/month.'
    },
    {
      q: 'What are the working hours?',
      a: 'Completely flexible! You set your own schedule. Most observers work 2-4 hours daily during after-school hours (3 PM - 8 PM). Weekend slots are also available.'
    },
    {
      q: 'Is training provided?',
      a: 'Yes! We provide free comprehensive training covering child psychology, active listening, documentation, and our platform. Training takes about 2 weeks and is fully online.'
    },
    {
      q: 'How do I get assigned children?',
      a: 'Our principal team matches you with children based on your availability, language preferences, and expertise. You\'ll typically have 5-15 children assigned.'
    },
    {
      q: 'What equipment do I need?',
      a: 'Just a smartphone or laptop with internet connection, a quiet space, and headphones. That\'s it!'
    }
  ];

  const requirements = [
    'Graduate degree (any field)',
    'Fluent in Hindi and/or English',
    'Available 1-4 hours daily',
    'Smartphone/laptop with internet',
    'Quiet, private space for calls',
    'Passion for child development'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
        
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-sm mb-6">
                <Zap className="w-4 h-4" /> Now Hiring in 50+ Cities
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Earn ₹30,000-₹50,000/month<br/>
                <span className="text-purple-200">Working From Home</span>
              </h1>
              <p className="text-xl text-purple-100 mb-8">
                Become a Sanjaya Observer. Help children express themselves while earning 
                competitive income with flexible hours. No experience required - we train you!
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  onClick={() => navigate('/observer/register')}
                  className="bg-white text-purple-700 hover:bg-purple-50 text-lg px-8 py-6"
                >
                  Apply Now - It's Free <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => setShowVideo(true)}
                  className="border-white text-white hover:bg-white/10 text-lg px-8 py-6"
                >
                  <Play className="w-5 h-5 mr-2" /> Watch Video
                </Button>
              </div>
              <p className="text-purple-200 text-sm mt-4">
                ✓ Free training included &nbsp; ✓ Start within 2 weeks &nbsp; ✓ Work from anywhere
              </p>
            </div>
            
            {/* Earnings Calculator */}
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <IndianRupee className="w-5 h-5" /> Earnings Calculator
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-purple-200 text-sm">Hours per day</label>
                    <div className="flex gap-2 mt-1">
                      {[1, 2, 3, 4].map(h => (
                        <button
                          key={h}
                          onClick={() => setEarnings({...earnings, hours: h})}
                          className={`flex-1 py-2 rounded-lg font-medium transition-all ${
                            earnings.hours === h 
                              ? 'bg-white text-purple-700' 
                              : 'bg-white/20 hover:bg-white/30'
                          }`}
                        >
                          {h}h
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-purple-200 text-sm">Days per week</label>
                    <div className="flex gap-2 mt-1">
                      {[3, 4, 5, 6].map(d => (
                        <button
                          key={d}
                          onClick={() => setEarnings({...earnings, days: d})}
                          className={`flex-1 py-2 rounded-lg font-medium transition-all ${
                            earnings.days === d 
                              ? 'bg-white text-purple-700' 
                              : 'bg-white/20 hover:bg-white/30'
                          }`}
                        >
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="pt-4 border-t border-white/20">
                    <p className="text-purple-200 text-sm">Your estimated monthly earnings</p>
                    <p className="text-4xl font-bold text-white">
                      ₹{monthlyEarnings.toLocaleString()}
                    </p>
                    <p className="text-purple-200 text-xs mt-1">
                      Based on average session rate. Actual earnings may vary.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <stat.icon className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-gray-600 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Become a Sanjaya Observer?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Join hundreds of observers who've found meaningful, flexible work
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                    <benefit.icon className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Certification Levels */}
      <section className="py-16 px-4 bg-gradient-to-br from-purple-50 to-indigo-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Grow Your Earnings Over Time
            </h2>
            <p className="text-xl text-gray-600">
              Progress through certification levels and increase your pay rate
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {certifications.map((cert, idx) => (
              <Card key={idx} className="overflow-hidden hover:scale-105 transition-transform">
                <div className={`bg-gradient-to-r ${cert.color} p-4 text-white text-center`}>
                  <span className="text-4xl">{cert.icon}</span>
                  <h3 className="text-xl font-bold mt-2">{cert.level}</h3>
                </div>
                <CardContent className="p-4 text-center">
                  <p className="text-gray-600 text-sm">Sessions</p>
                  <p className="font-semibold text-gray-900">{cert.sessions}</p>
                  <p className="text-gray-600 text-sm mt-2">Rate per session</p>
                  <p className="text-2xl font-bold text-purple-600">{cert.rate}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Start Earning in 3 Weeks
            </h2>
            <p className="text-xl text-gray-600">
              Our simple 5-step process to become an observer
            </p>
          </div>
          
          <div className="relative">
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-purple-200 -translate-y-1/2 z-0" />
            <div className="grid md:grid-cols-5 gap-6 relative z-10">
              {steps.map((step, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold shadow-lg">
                    {step.num}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{step.title}</h3>
                  <p className="text-gray-600 text-sm mb-2">{step.desc}</p>
                  <span className="text-purple-600 text-xs font-medium">{step.time}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="text-center mt-12">
            <Button 
              size="lg" 
              onClick={() => navigate('/observer/register')}
              className="bg-purple-600 hover:bg-purple-700 text-lg px-8"
            >
              Start Your Application <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Hear From Our Observers
            </h2>
            <p className="text-xl text-gray-600">
              Real stories from people like you
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(t.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{t.text}"</p>
                  <div className="flex items-center gap-3 pt-4 border-t">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold text-lg">
                      {t.image}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{t.name}</p>
                      <p className="text-gray-600 text-sm">{t.role}</p>
                    </div>
                    <div className="ml-auto text-right">
                      <p className="text-purple-600 font-bold">{t.earnings}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What You Need to Apply
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            {requirements.map((req, idx) => (
              <div key={idx} className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                <span className="text-gray-700">{req}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <Card key={idx} className="overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50"
                >
                  <span className="font-medium text-gray-900">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === idx && (
                  <div className="px-4 pb-4">
                    <p className="text-gray-600">{faq.a}</p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-purple-600 to-indigo-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Join 500+ observers who are earning while making a difference. 
            Apply today and start within 3 weeks!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg" 
              onClick={() => navigate('/observer/register')}
              className="bg-white text-purple-700 hover:bg-purple-50 text-lg px-8 py-6"
            >
              Apply Now - Free <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => navigate('/observer/application-status')}
              className="border-white text-white hover:bg-white/10 text-lg px-8 py-6"
            >
              Check Application Status
            </Button>
          </div>
          <p className="text-purple-200 text-sm mt-6">
            Questions? Call us at <a href="tel:+918291188188" className="underline">+91 8291 188 188</a> or 
            email <a href="mailto:join@sanjaya.in" className="underline">join@sanjaya.in</a>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ObserverLanding;
